import { useEffect, useState } from "react";

/**
 * Height the fixed header takes, in pixels. A heading only counts as active once it has cleared it,
 * otherwise the index would mark a section the reader cannot see.
 *
 * YOSHI-10464: it has to stay at or below where a jump to an anchor leaves the heading (the header
 * plus the air `scroll-mt-6` adds), or the section the reader has just jumped to would land above
 * this line and the index would keep the previous one marked.
 */
const HEADER_OFFSET_PX: number = 100;

/** How much of the viewport bottom is ignored, so only the section being read is considered. */
const BOTTOM_MARGIN: string = "-55%";

/**
 * YOSHI-10464: tells which section of the page is being read. IntersectionObserver is a browser API
 * and needs no dependency; it also reports only what changed, so scrolling does not run a layout
 * measurement of every heading on every frame.
 *
 * @param sectionIds Ids of the sections in reading order. It must be a stable reference.
 * @returns The id of the active section, the first one while nothing has been scrolled past.
 */
export const useActiveSection = (sectionIds: string[]): string => {
  const [activeId, setActiveId] = useState<string>(sectionIds[0] ?? "");

  useEffect(() => {
    const visibleIds = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visibleIds.add(entry.target.id);
          } else {
            visibleIds.delete(entry.target.id);
          }
        });

        // The topmost visible section wins. When none is visible the last one stays marked, which is
        // what a reader in the middle of a long block expects.
        const firstVisibleId = sectionIds.find((id) => visibleIds.has(id));

        if (firstVisibleId !== undefined) {
          setActiveId(firstVisibleId);
        }
      },
      { rootMargin: `-${HEADER_OFFSET_PX}px 0px ${BOTTOM_MARGIN} 0px` }
    );

    sectionIds.forEach((id) => {
      const element = document.getElementById(id);

      if (element !== null) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [sectionIds]);

  return activeId;
};
